<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TaskCard from './components/TaskCard.vue';
import TaskEditDialog from './components/TaskEditDialog.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const boardId = computed(() => Number(route.params.boardId));
const currentAccountId = useMapGetter('getCurrentAccountId');
const board = computed(() =>
  store.getters['kanban/getBoardById'](boardId.value)
);
const steps = useMapGetter('kanban/getSteps');
const uiFlags = useMapGetter('kanban/getUIFlags');
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');

const taskDialogRef = ref(null);
const agentFilter = ref('');
const inboxFilter = ref('');

// Listas locais por etapa para o vuedraggable
const tasksByStep = ref({});

const matchesFilters = task => {
  if (
    agentFilter.value &&
    !(task.assigned_agent_ids || []).includes(Number(agentFilter.value))
  ) {
    return false;
  }
  if (
    inboxFilter.value &&
    !(task.conversations || []).some(
      conversation => conversation.inbox_id === Number(inboxFilter.value)
    )
  ) {
    return false;
  }
  return true;
};

const syncLocalTasks = () => {
  const next = {};
  steps.value.forEach(step => {
    next[step.id] = store.getters['kanban/getTasksByStep'](step.id).filter(
      matchesFilters
    );
  });
  tasksByStep.value = next;
};

const allTasks = useMapGetter('kanban/getTasks');
watch([allTasks, steps, agentFilter, inboxFilter], syncLocalTasks, {
  deep: true,
});

const fetchBoardData = async () => {
  await Promise.all([
    store.dispatch('kanban/fetchBoard', boardId.value),
    store.dispatch('kanban/fetchTasks', { board_id: boardId.value }),
  ]);
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  syncLocalTasks();
};

onMounted(fetchBoardData);
watch(boardId, fetchBoardData);

const onDragEnd = async event => {
  const stepId = Number(event.to?.dataset?.stepId);
  const { newIndex } = event;
  const movedTask = tasksByStep.value[stepId]?.[newIndex];
  if (!stepId || !movedTask) return;

  const nextTask = tasksByStep.value[stepId]?.[newIndex + 1];
  try {
    await store.dispatch('kanban/moveTask', {
      taskId: movedTask.id,
      boardStepId: stepId,
      insertBeforeTaskId: nextTask?.id || null,
    });
  } catch (error) {
    useAlert(error.message);
    syncLocalTasks();
  }
};

const openConversation = conversation => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: currentAccountId.value,
      conversation_id: conversation.display_id,
    },
  });
};

const openSettings = () => {
  router.push({
    name: 'kanban_board_settings',
    params: { accountId: currentAccountId.value, boardId: boardId.value },
  });
};

const goBack = () => {
  router.push({
    name: 'kanban_view',
    params: { accountId: currentAccountId.value },
  });
};

const addTask = (step = null) => {
  taskDialogRef.value?.open(null, step?.id || steps.value[0]?.id);
};

const editTask = task => {
  taskDialogRef.value?.open(task);
};

const deleteTask = async task => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.TASK.DELETE_CONFIRM'))) return;
  try {
    await store.dispatch('kanban/deleteTask', task.id);
  } catch (error) {
    useAlert(error.message);
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="flex items-center justify-between gap-3 px-6 py-3 border-b border-n-weak"
    >
      <div class="flex items-center gap-2 min-w-0">
        <Button
          ghost
          slate
          xs
          icon="i-lucide-chevron-left"
          :title="t('KANBAN.BOARD.BACK')"
          @click="goBack"
        />
        <h1 class="text-base font-medium truncate text-n-slate-12">
          {{ board?.name }}
        </h1>
        <span
          class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
        >
          {{ allTasks.length }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <select
          v-model="agentFilter"
          class="px-2 py-1.5 text-xs border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-11"
        >
          <option value="">{{ t('KANBAN.FILTERS.ALL_AGENTS') }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <select
          v-model="inboxFilter"
          class="px-2 py-1.5 text-xs border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-11"
        >
          <option value="">{{ t('KANBAN.FILTERS.ALL_INBOXES') }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <Button
          ghost
          slate
          sm
          icon="i-lucide-settings"
          class="flex-shrink-0"
          :title="t('KANBAN.BOARD.SETTINGS')"
          @click="openSettings"
        />
        <Button
          solid
          blue
          sm
          icon="i-lucide-plus"
          class="flex-shrink-0 whitespace-nowrap"
          @click="addTask()"
        >
          {{ t('KANBAN.BOARD.ADD_TASK') }}
        </Button>
      </div>
    </header>

    <div
      v-if="uiFlags.isFetchingBoard"
      class="flex items-center justify-center flex-1 gap-2 text-n-slate-11"
    >
      <Spinner />
      {{ t('KANBAN.BOARD.LOADING') }}
    </div>

    <div
      v-else-if="!steps.length"
      class="flex flex-col items-center justify-center flex-1 gap-2 text-center"
    >
      <Icon icon="i-lucide-columns-3" class="size-10 text-n-slate-9" />
      <p class="text-base font-medium text-n-slate-12">
        {{ t('KANBAN.BOARD.NO_STEPS_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ t('KANBAN.BOARD.NO_STEPS_DESCRIPTION') }}
      </p>
      <Button
        solid
        blue
        sm
        icon="i-lucide-settings"
        class="mt-2"
        @click="openSettings"
      >
        {{ t('KANBAN.BOARD.SETTINGS') }}
      </Button>
    </div>

    <div v-else class="flex flex-1 gap-3 px-4 py-4 overflow-x-auto">
      <div
        v-for="step in steps"
        :key="step.id"
        class="flex flex-col flex-shrink-0 overflow-hidden w-72 max-h-full rounded-lg bg-n-solid-2"
      >
        <div
          class="flex items-center justify-between gap-2 px-3 py-2"
          :style="{ backgroundColor: step.color }"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-sm font-medium text-white truncate">
              {{ step.name }}
            </span>
            <span
              class="flex-shrink-0 px-1.5 text-xs text-white rounded-full bg-black/20"
            >
              {{ (tasksByStep[step.id] || []).length }}
            </span>
          </div>
          <span
            class="flex-shrink-0 p-1 rounded hover:bg-black/20"
            role="button"
            :title="t('KANBAN.BOARD.ADD_TASK')"
            @click="addTask(step)"
          >
            <Icon icon="i-lucide-plus" class="text-white size-4" />
          </span>
        </div>

        <Draggable
          :list="tasksByStep[step.id] || []"
          item-key="id"
          group="kanban-tasks"
          class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-[4rem]"
          ghost-class="opacity-50"
          :data-step-id="step.id"
          @end="onDragEnd"
        >
          <template #item="{ element }">
            <TaskCard
              :task="element"
              @open-conversation="openConversation"
              @edit="editTask"
              @delete="deleteTask"
            />
          </template>
          <template #footer>
            <button
              type="button"
              class="flex items-center justify-center gap-1 py-2 text-xs text-center rounded text-n-slate-10 hover:bg-n-alpha-1"
              @click="addTask(step)"
            >
              <Icon icon="i-lucide-plus" class="size-3" />
              {{ t('KANBAN.BOARD.ADD_TASK') }}
            </button>
          </template>
        </Draggable>
      </div>
    </div>

    <TaskEditDialog ref="taskDialogRef" :board-id="boardId" :steps="steps" />
  </section>
</template>
